import { Button } from '@/components/ui/button';
import { 
  ChevronLeft, 
  Eye, 
  Save, 
  Globe, 
  ArrowLeft,
  Sparkles,
  LayoutGrid,
  Palette,
  Settings2,
  Cpu,
  Eye as EyeIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BuilderLayoutProps {
  children: React.ReactNode;
  formTitle?: string;
  isSaving?: boolean;
  isPublished?: boolean;
  onSave: (publish: boolean) => void;
  onPreview: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

export const BuilderLayout = ({ 
  children, 
  formTitle, 
  isSaving, 
  isPublished,
  onSave, 
  onPreview,
  activeTab,
  onTabChange
}: BuilderLayoutProps) => {
  const navigate = useNavigate();

  const navItems = [
    { id: 'elements', icon: LayoutGrid, label: 'Elements' },
    { id: 'ai', icon: Cpu, label: 'AI Architect' },
    { id: 'preview', icon: EyeIcon, label: 'Preview' },
    { id: 'style', icon: Palette, label: 'Style' },
    { id: 'settings', icon: Settings2, label: 'Settings' },
  ];

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden font-sans selection:bg-primary/20">
      {/* Editorial Header */}
      <header className="h-14 sm:h-20 border-b border-border/10 bg-background/50 backdrop-blur-xl flex items-center justify-between px-4 sm:px-8 shrink-0 z-50">
        <div className="flex items-center gap-3 sm:gap-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/dashboard')}
            className="hover:bg-surface-high rounded-xl h-9 w-9 sm:h-10 sm:w-10 transition-all"
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          <div className="h-6 w-px bg-border/20 hidden sm:block" />
          <div className="flex flex-col">
            <span className="text-[9px] sm:text-[10px] font-bold text-primary uppercase tracking-widest leading-none mb-0.5 sm:mb-1">Editor</span>
            <h1 className="text-sm sm:text-lg font-display font-bold tracking-tight truncate max-w-[140px] sm:max-w-none">{formTitle || 'Untitled Form'}</h1>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-low border border-border/10">
             <div className={cn("w-1.5 h-1.5 rounded-full", isPublished ? "bg-primary animate-pulse" : "bg-muted-foreground")} />
             <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
               {isPublished ? 'Live' : 'Draft'}
             </span>
          </div>

          <Button 
            variant="ghost" 
            onClick={onPreview}
            className="rounded-xl h-9 sm:h-10 gap-2 hover:bg-surface-high transition-all px-3"
          >
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Preview</span>
          </Button>

          <Button 
            variant="outline" 
            onClick={() => onSave(false)} 
            disabled={isSaving}
            className="rounded-xl h-9 sm:h-10 border-border/20 hover:bg-surface-high transition-all px-3"
          >
            <Save className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Save Draft</span>
          </Button>

          <Button 
            onClick={() => onSave(true)} 
            disabled={isSaving}
            className="btn-gradient rounded-xl h-9 sm:h-10 px-3 sm:px-6 gap-2"
          >
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">Publish Form</span>
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Navigation Rail - hidden on mobile, shown on sm+ */}
        <aside className="hidden sm:flex w-20 border-r border-border/10 bg-background/50 backdrop-blur-xl flex-col items-center py-8 gap-4 shrink-0 z-40">
           {navItems.map((item) => (
             <button
               key={item.id}
               onClick={() => onTabChange?.(item.id)}
               className={cn(
                 "group relative h-12 w-12 rounded-2xl flex items-center justify-center transition-all",
                 activeTab === item.id 
                   ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-110" 
                   : "text-muted-foreground hover:bg-surface-low hover:text-foreground"
               )}
             >
                <item.icon className="h-5 w-5" />
                <div className="absolute left-full ml-4 px-3 py-1.5 bg-surface-highest text-foreground text-[10px] font-bold uppercase tracking-widest rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 border border-border/10">
                   {item.label}
                </div>
             </button>
           ))}
        </aside>

        {/* Full-Screen Workspace */}
        <main className="flex-1 flex overflow-hidden bg-[url('/grid.svg')] bg-center bg-fixed">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <nav className="sm:hidden flex items-center justify-around border-t border-border/10 bg-background/90 backdrop-blur-xl h-16 shrink-0 z-50 px-2">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onTabChange?.(item.id)}
            className={cn(
              "flex flex-col items-center justify-center gap-1 flex-1 h-full rounded-xl transition-all",
              activeTab === item.id
                ? "text-primary"
                : "text-muted-foreground"
            )}
          >
            <item.icon className="h-5 w-5" />
            <span className="text-[9px] font-bold uppercase tracking-widest">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};
